import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiMapPin, FiHeart, FiGift, FiHome, FiCamera, FiMusic } from 'react-icons/fi';
import OtherOccasions from '../components/OtherOccasions';
import SEO from '../components/SEO';
import FAQSection from '../components/FAQSection';
import { celebrationsFAQs } from '../data/faqs';

// Intimate Celebrations — SEO/GEO landing page for queries like:
// "premium villa for intimate celebrations Lonavala",
// "birthday villa Lonavala", "anniversary villa Lonavala",
// "bachelor party villa Lonavala", "pre-wedding villa Lonavala"
const Celebrations = () => {
  return (
    <div data-testid="celebrations-page" className="pt-20">
      <SEO
        title="Villa in Lonavala for Birthday Party, Bachelor Party & Private Party — Private Pool | Royal Garden Villas"
        description="Premium villas for birthdays, anniversaries, mehendi, haldi, sangeet, bachelor parties and baby showers in Lonavala. Private pools, photogenic lawns, exclusive estate for 12–48 guests."
        path="/intimate-celebrations-lonavala"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Intimate Celebrations', url: '/intimate-celebrations-lonavala' },
        ]}
      />
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[400px] overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/8uotvybj_2.jpg"
          alt="Intimate celebration venue at Royal Garden Villas Lonavala"
          className="w-full h-full object-cover"
          width="1920" height="1080"
          loading="eager" fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A2E]/85 via-[#1A1A2E]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-7xl mx-auto">
          <p className="text-[#F57C20] text-xs font-semibold tracking-[0.2em] uppercase mb-3">Celebrations &amp; Events</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Premium Villas for <span className="text-[#F57C20]">Intimate Celebrations in Lonavala</span>
          </h1>
          <p className="text-white/85 font-light mt-4 max-w-2xl text-sm md:text-base leading-relaxed">
            Milestone birthdays, anniversaries, pre-wedding mehendi and haldi, bachelorette weekends, baby showers and small destination weddings — set on a gated estate with private pools, lawns and exclusive use of the property.
          </p>
        </div>
      </section>

      {/* Celebration types */}
      <section className="py-16 md:py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-light text-[#1A1A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Every Occasion, <span className="text-[#F57C20]">Beautifully Hosted</span>
          </h2>
          <p className="text-[#6B7280] text-sm leading-relaxed max-w-3xl mb-10">
            From quiet, candlelit anniversaries to vibrant pre-wedding sangeets — Royal Garden Villas is one of the most preferred <strong>luxury villa rentals in Lonavala for intimate celebrations</strong>. Book a single villa for 12–20 guests, or the entire 12BHK estate for groups up to 48.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: FiGift, label: 'Birthdays' },
              { icon: FiHeart, label: 'Anniversaries' },
              { icon: FiMusic, label: 'Sangeet / Mehendi' },
              { icon: FiCamera, label: 'Baby Showers' },
              { icon: FiHome, label: 'Pre-Wedding' },
              { icon: FiGift, label: 'Bachelorette' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-4 text-center border border-gray-100">
                <item.icon className="w-5 h-5 text-[#F57C20] mx-auto mb-2" />
                <p className="text-[#1A1A2E] text-xs font-semibold">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Long-form content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-light text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Why Royal Garden Villas Is Lonavala&apos;s Favourite for Private Celebrations
          </h2>
          <div className="space-y-4 text-[#374151] text-sm md:text-base leading-relaxed">
            <p>
              A celebration is more than a checklist of decor and food — it&apos;s about the people who matter most being together, in a place that lets the moment breathe. Royal Garden Villas is a luxury private estate in Lonavala designed exactly for that. No hotel lobbies, no shared lifts, no other guests overhearing your speeches. Just your family, your friends, and the property — entirely yours for the weekend.
            </p>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Milestone Birthdays — 30th, 40th, 50th, 60th</h3>
            <p>
              A milestone birthday calls for a private setting where the photographs feel candid and the conversations linger. The lawn at the <Link to="/villas/5bhk-garden-delight-lonavala" className="text-[#F57C20] hover:underline">5BHK Garden Delight</Link> or the <Link to="/villas/12bhk-celebration-villa-lonavala" className="text-[#F57C20] hover:underline">12BHK Celebration Villa</Link> works beautifully for birthday dinners under fairy lights. Pair it with a BBQ setup (₹3,000 per meal) and a bonfire (₹2,000 per session) — and you have a quietly memorable celebration without the cliché of a banquet hall.
            </p>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Pre-Wedding Functions — Mehendi, Haldi, Sangeet</h3>
            <p>
              Lonavala has become one of the most chosen destinations for <strong>intimate pre-wedding functions near Mumbai and Pune</strong>. Royal Garden Villas is a popular venue for mehendi mornings on the lawn, haldi by the pool, and sangeet evenings indoors with the music turned just loud enough to dance to. The 12BHK estate works wonderfully because it lets you host the bride&apos;s side and the groom&apos;s side in separate villas while still gathering everyone together on the common lawn.
            </p>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Anniversaries — Quiet or Festive, Your Choice</h3>
            <p>
              Some couples want a candlelit dinner on a private lawn with just close family. Others want a full-blown anniversary party with 20 of their favourite people. Royal Garden Villas can accommodate either. The 3BHK works for an intimate dozen; the 5BHK for a 20-person celebration; the 12BHK if you want the whole crew.
            </p>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Bachelor &amp; Bachelorette Weekends</h3>
            <p>
              Private pool, soundproof lawns (your neighbours are 200 metres away), a fully stocked kitchen for impromptu cocktail experiments, and gates that keep the outside world outside. Bring your playlist, bring the games, bring the matching pyjamas — we won&apos;t judge. Just respect the 10:30 PM quiet-hours rule and we&apos;re all good.
            </p>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Baby Showers &amp; Family Celebrations</h3>
            <p>
              For baby showers and intimate family functions, the 4BHK and 5BHK villas offer the right scale — large enough for 15–25 guests, intimate enough that the mom-to-be is comfortable and the photos feel personal. Lawns can be styled with simple drapes and floral arrangements (we&apos;re happy to refer trusted local decor partners).
            </p>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">What Sets Royal Garden Villas Apart for Events</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Exclusive use</strong> — the entire villa (or estate, if you book the 12BHK) is yours for the duration. No other guests sharing the pool, lobby or lawn.</li>
              <li><strong>Photogenic backdrops</strong> — architectural facades, manicured lawns, valley views and a swimming pool that catches the evening light beautifully for couple shoots and group portraits.</li>
              <li><strong>Catering flexibility</strong> — host your own caterer, hire ours, or use our maid service for smaller groups. The kitchen is fully equipped and gas-connected.</li>
              <li><strong>Sound &amp; setup</strong> — we&apos;re reasonable about music until 10:30 PM. Power sockets and lighting on the lawn allow for DJ setups for sangeet or birthday parties.</li>
              <li><strong>Transparent rates</strong> — what you see online is what you pay. No surprise event fees if you keep within the booked villa(s) and respect the house rules.</li>
            </ul>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Recommended Add-ons for Celebrations</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>BBQ setup (Optional)</strong> (₹3,000 per meal) — sigdi, charcoal, skewers and labour. Great for grilled-chicken evening parties.</li>
              <li><strong>Bonfire (Optional)</strong> (₹2,000 per session) — perfect for winter sangeets and post-dinner conversations.</li>
              <li><strong>Extra Butler service (Optional)</strong> (₹2,000 per day) — for groups that want a dedicated helping-hand exclusively for them.</li>
              <li><strong>Decor partners</strong> — we can refer trusted local decorators for balloons, floral arches, signage and theme setups. (Pricing varies; decor is paid directly to the vendor.)</li>
            </ul>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Booking &amp; Planning Tips</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Book 4–6 weeks in advance</strong> for weekends and 8+ weeks for long weekends or peak season (December–January, monsoon long weekends).</li>
              <li><strong>50% advance, 50% on arrival</strong> — both via Razorpay, no cash. You can also pay 100% upfront if preferred.</li>
              <li><strong>Cancellations</strong> — full refund if cancelled at least 7 days before check-in. No refund within 7 days.</li>
              <li><strong>Indemnity &amp; ID</strong> — every booking confirms by uploading a signature and government ID before payment. Quick, one-time, and fully digital.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Pick villa */}
      <section className="py-14 md:py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-light text-[#1A1A2E] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            Choose Your <span className="text-[#F57C20]">Celebration Venue</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { name: '3BHK Garden Escape', cap: '8–12 guests · Intimate dinners, small birthdays', slug: '3bhk-garden-escape-lonavala' },
              { name: '4BHK Garden Retreat', cap: '10–16 guests · Baby showers, small anniversaries', slug: '4bhk-garden-retreat-lonavala' },
              { name: '5BHK Garden Delight', cap: '12–20 guests · Mid-size birthdays, anniversaries', slug: '5bhk-garden-delight-lonavala' },
              { name: '12BHK Celebration Villa', cap: '20–48 guests · Sangeet, large birthdays, family functions', slug: '12bhk-celebration-villa-lonavala' },
            ].map((v) => (
              <Link key={v.slug} to={`/villas/${v.slug}`} className="block bg-white rounded-xl p-5 border border-gray-100 hover:border-[#F57C20] transition-all" data-testid={`celebration-villa-${v.slug}`}>
                <FiHome className="w-5 h-5 text-[#F57C20] mb-3" />
                <h3 className="text-[#1A1A2E] text-base font-semibold mb-1">{v.name}</h3>
                <p className="text-[#6B7280] text-xs mb-3">{v.cap}</p>
                <span className="text-[#F57C20] text-xs font-semibold inline-flex items-center gap-1">View Details <FiArrowRight className="w-3 h-3" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Internal linking — other occasions */}
      <OtherOccasions currentPath="/intimate-celebrations-lonavala" heading="Also Perfect For" />

      {/* CTA */}
      <section className="py-16 border-t border-white/5 bg-[#1A1A2E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <FiGift className="w-6 h-6 text-[#F57C20] mx-auto mb-3" />
          <h3 className="text-xl font-light text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Plan Your Celebration</h3>
          <p className="text-white/50 font-light mb-8 text-sm max-w-xl mx-auto">Share your dates, guest count and event type — we&apos;ll suggest the right villa and add-ons.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking" className="bg-[#F57C20] text-white px-8 py-3 rounded-lg text-sm font-semibold hover:bg-[#E06A10] transition-all flex items-center justify-center gap-2" data-testid="celebrations-cta-book">
              Check Availability <FiArrowRight />
            </Link>
            <a href="tel:+919820088292" className="border-2 border-white/20 text-white px-8 py-3 rounded-lg text-sm font-semibold hover:border-[#F57C20] hover:text-[#F57C20] transition-all flex items-center justify-center gap-2">
              <FiMapPin className="w-4 h-4" /> Call +91 98200 88292
            </a>
          </div>
        </div>
      </section>

      {/* FAQs — Auto-emits FAQPage JSON-LD for AI Overviews & rich results */}
      <FAQSection
        title="Celebrations at Royal Garden Villas — Frequently Asked Questions"
        items={celebrationsFAQs}
        testid="celebrations-faqs"
      />
    </div>
  );
};

export default Celebrations;
